// Mirrored from mju-ai-workbench/shared/schemas/secretSeal.ts (b50a7d34, D177 v2: owner-bound seals) — keep the two identical but for this line.
/**
 * Personal secrets sealed to a person's own devices (D177 phase 3,
 * docs/plans/2026-10-secrets-scopes.md §3 "Delivery paths" and the threat
 * model's "A compromised hub"): the person's home — the hub on Teams, the
 * organisation on Enterprise — keeps and relays a personal secret's value
 * as ciphertext only its owner's devices (and, for a secret that says
 * "Follow me", the nodes the person lets run their work) can open.
 *
 * **Device sealing keys.** Every workbench and node makes one X25519 key
 * pair, once, and keeps the private half where the machine keeps its own keys
 * (never in a host's snapshot, never sent anywhere). The public half is
 * published to the home:
 *
 *   PUT /secrets/v1/sealing-keys/self   { publicKey, kid? } → 200 SealingKeyEntry: the calling device's key
 *                                       (a new key replaces the old; `kid`, when sent, must be the key's own)
 *   GET /secrets/v1/sealing-keys        → SealingKeysList: the keys of the caller's own devices (`keys`) and of
 *                                       the nodes they let run their work (`nodes`); a node is refused (a node
 *                                       never seals a person's secret)
 *
 * and, at the organisation, as `sealingKey` on fleet registration and
 * heartbeat (src/platform/fleet.ts), which a platform that does not know it
 * ignores. A home that answers 404 for these routes keeps no device keys: a
 * personal secret is sent to it as a plain value, as before — unless that home
 * once listed keys for this person, which a workbench remembers: it then
 * refuses rather than send the value plain (a home cannot downgrade it by
 * forgetting the keys).
 *
 * `sealFormats` on the list (AIOE 0.24.0: `[1, 2]`) names the sealed formats
 * the home stores and releases. A home without it understands `mjuseal1.`
 * only (an older one would take a `mjuseal2.` value for a plain one and
 * refuse it, `seal_expected`), so a workbench writes v2 to it only when the
 * list says 2; a hub (ours) takes v2 always.
 *
 * **Key id.** `kid` = the first 22 characters of base64url(SHA-256(the raw
 * 32-byte public key)). A home recomputes it and refuses a `kid` that differs.
 *
 * **A sealed value** is a string that starts with `mjuseal<digit>.`
 * ({@link isSealedSecretValue}; a home treats every such value as sealed):
 *
 *   mjuseal1.<base64url(header JSON)>.<base64url(12-byte nonce | ciphertext | 16-byte tag)>
 *   mjuseal2.<base64url(header JSON)>.<base64url(12-byte nonce | ciphertext | 16-byte tag)>
 *
 * **Version 2** binds the value to its **owner**: `ctx.owner` is the person it
 * is sealed for, as the home names them (`ownerPrincipal`, the principal a node
 * names in `x-aioe-principal`). It is part of the binding string both layers
 * authenticate, and the binding and the key derivation's `info` start with the
 * version, so neither the owner nor the version can be changed, dropped or
 * swapped without the value failing to open. A node opens a v2 value only into
 * the set it holds for that person; another person's is refused and audited —
 * so a home cannot hand person A's "Follow me" value to a node in B's release.
 * Version 1 has no owner and still opens (values sealed before v2).
 *
 * The header ({@link sealedHeaderSchema}) names the algorithm, the binding
 * (`ctx`: the secret's name, and its id and version where the sealer knew
 * them), one ephemeral X25519 public key, and per recipient its `kid` and the
 * content key wrapped for it: X25519(ephemeral, recipient) → HKDF-SHA256
 * (salt = ephemeral public | recipient public, info = "mojoup-secret-seal-v<version>|" + kid)
 * → AES-256-GCM over the 32-byte content key. The content is AES-256-GCM
 * under that content key. Both are authenticated with the same binding
 * string, so a changed name, id, version, algorithm or ephemeral key fails to
 * open. A home stores and releases a sealed value exactly as it does a plain
 * one (it may seal it again at rest under its own key); it never opens it.
 *
 * Pure: zod only, no crypto (the sealing is src/secrets/seal.ts).
 */
import { z } from "zod";
/** What a version 1 sealed value starts with. */
export const SEALED_SECRET_PREFIX = "mjuseal1.";
/** What a version 2 sealed value (bound to its owner) starts with. */
export const SEALED_SECRET_PREFIX_V2 = "mjuseal2.";
/** The sealed formats this workbench writes and opens. */
export const SEAL_FORMATS = [1, 2];
/** What every sealed value starts with, any version: a plain value never does (a value that would is refused by the sealer). */
export const SEALED_VALUE_PATTERN = /^mjuseal\d\./;
/** The prefix of a sealed value of this version. */
export function sealedPrefix(format) {
    return format === 2 ? SEALED_SECRET_PREFIX_V2 : SEALED_SECRET_PREFIX;
}
/** The one algorithm of version 1. */
export const SEAL_ALG = "X25519-HKDF-SHA256-A256GCM";
/** How many devices and nodes one value may be sealed to. */
export const SEAL_MAX_RECIPIENTS = 64;
const b64url = (min, max) => z
    .string()
    .min(min)
    .max(max)
    .regex(/^[A-Za-z0-9_-]+$/);
/** A raw 32-byte X25519 public key, base64url without padding (43 characters). */
export const sealingPublicKeySchema = b64url(43, 43);
/** A key id: the first 22 characters of base64url(SHA-256(public key)). */
export const sealingKidSchema = b64url(22, 22);
/** A device's published sealing key. */
export const deviceSealingKeySchema = z.object({
    alg: z.literal("X25519"),
    kid: sealingKidSchema,
    publicKey: sealingPublicKeySchema,
});
/** `PUT /secrets/v1/sealing-keys/self` body. */
export const sealingKeyRegistrationSchema = z.object({
    publicKey: sealingPublicKeySchema,
    kid: sealingKidSchema.optional(),
});
/** One device's key as a home lists it. */
export const sealingKeyEntrySchema = deviceSealingKeySchema.extend({
    deviceId: z.string().min(1).max(100),
    kind: z.enum(["desktop", "node", "phone"]).optional(),
    /** The device's name, for a person ("JP's laptop"). */
    name: z.string().max(120).optional(),
    registeredAt: z.string(),
});
/** `GET /secrets/v1/sealing-keys` answer. */
export const sealingKeysListSchema = z.object({
    /** The caller's own devices. */
    keys: z.array(sealingKeyEntrySchema).max(SEAL_MAX_RECIPIENTS),
    /** The nodes the caller lets run their work: recipients of a secret that says "Follow me". */
    nodes: z.array(sealingKeyEntrySchema).max(SEAL_MAX_RECIPIENTS).default([]),
    /**
     * The sealed formats the home stores and releases (AIOE 0.24.0: `[1, 2]`). Absent: version 1 only. Read leniently:
     * anything but a list of small whole numbers is taken as absent, never a refusal of the whole list.
     */
    sealFormats: z.preprocess((v) => (Array.isArray(v) ? v.filter((x) => Number.isInteger(x) && x > 0 && x < 100) : undefined), z.array(z.number().int()).optional()),
});
/** A person's principal as a home names them (`ownerPrincipal`, `x-aioe-principal`). */
export const sealOwnerSchema = z.string().min(1).max(200);
/** What a sealed value is bound to. `owner`: the person it is sealed for (version 2 only; version 1 has none). */
export const sealContextSchema = z.object({
    name: z.string().min(1).max(200),
    id: z.string().min(1).max(200).optional(),
    version: z.number().int().positive().optional(),
    owner: sealOwnerSchema.optional(),
});
const recipientsSchema = z
    .array(z.object({ kid: sealingKidSchema, iv: b64url(16, 16), k: b64url(64, 64) }))
    .min(1)
    .max(SEAL_MAX_RECIPIENTS);
/** A version 1 header: no owner (one that names one is malformed). */
const sealedHeaderV1Schema = z.object({
    v: z.literal(1),
    alg: z.literal(SEAL_ALG),
    ctx: sealContextSchema.omit({ owner: true }).strict(),
    /** The ephemeral X25519 public key. */
    epk: sealingPublicKeySchema,
    /** Per recipient: its key id, the wrap's nonce and the wrapped content key (ciphertext | tag). */
    r: recipientsSchema,
});
/** A version 2 header: the owner is required. */
const sealedHeaderV2Schema = z.object({
    v: z.literal(2),
    alg: z.literal(SEAL_ALG),
    ctx: sealContextSchema.extend({ owner: sealOwnerSchema }).strict(),
    epk: sealingPublicKeySchema,
    r: recipientsSchema,
});
/** A sealed value's header (the part between the prefix and the first dot after it); `v` matches the prefix. */
export const sealedHeaderSchema = z.discriminatedUnion("v", [sealedHeaderV1Schema, sealedHeaderV2Schema]);
/** Whether a value is sealed — any `mjuseal<digit>.` (what a home or a machine checks before treating it as plain). */
export function isSealedSecretValue(value) {
    return typeof value === "string" && SEALED_VALUE_PATTERN.test(value);
}
/** The version a sealed value says it is, by its prefix; undefined for a plain value or a version this workbench does not know. */
export function sealedFormatOf(value) {
    if (typeof value !== "string")
        return undefined;
    if (value.startsWith(SEALED_SECRET_PREFIX))
        return 1;
    if (value.startsWith(SEALED_SECRET_PREFIX_V2))
        return 2;
    return undefined;
}
/**
 * The canonical binding string both layers authenticate: the version's prefix, the algorithm, the ephemeral key and
 * the context in a fixed order — version 2's owner included, so it cannot be swapped or dropped.
 */
export function sealBinding(alg, epk, ctx, format = 1) {
    const c = { name: ctx.name };
    if (ctx.id !== undefined)
        c.id = ctx.id;
    if (ctx.version !== undefined)
        c.version = ctx.version;
    if (format === 2)
        c.owner = ctx.owner;
    return `${sealedPrefix(format)}|${alg}|${epk}|${JSON.stringify(c)}`;
}
//# sourceMappingURL=secretSeal.js.map